/**
 * General formatting utility functions
 */

/**
 * Truncate string with ellipsis
 */
export function truncate(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string | null | undefined): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

/**
 * Convert string to title case
 */
export function toTitleCase(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

/**
 * Format file size (bytes to human readable)
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format number with commas
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  return value.toLocaleString('en-US');
}

/**
 * Remove all whitespace
 */
export function removeWhitespace(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/\s/g, '');
}

/**
 * Remove special characters
 */
export function removeSpecialChars(text: string | null | undefined): string {
  if (!text) return '';
  return text.replace(/[^a-zA-Z0-9\s]/g, '');
}

/**
 * Slugify string (for URLs)
 */
export function slugify(text: string | null | undefined): string {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Mask sensitive data (e.g., email, phone)
 */
export function maskSensitiveData(value: string | null | undefined, visibleChars: number = 4): string {
  if (!value) return '';
  if (value.length <= visibleChars) return '*'.repeat(value.length);
  
  const visible = value.slice(-visibleChars);
  const masked = '*'.repeat(value.length - visibleChars);
  return masked + visible;
}

/**
 * Mask email address
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  
  const [localPart, domain] = email.split('@');
  if (!domain) return maskSensitiveData(email);
  
  const maskedLocal = maskSensitiveData(localPart, 2);
  return `${maskedLocal}@${domain}`;
}

/**
 * Format duration in milliseconds to human readable
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

/**
 * Get initials from name
 */
export function getInitials(name: string | null | undefined, maxInitials: number = 2): string {
  if (!name) return '';
  
  const words = name.trim().split(/\s+/);
  const initials = words
    .slice(0, maxInitials)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
  
  return initials;
}

/** Contract status enum ID -> display label (API returns status as int). */
const CONTRACT_STATUS_LABELS: Record<number, string> = {
  0: 'Draft',
  1: 'Awaiting Customer Signature',
  2: 'Awaiting Marina Signature',
  3: 'Awaiting Initial Payment',
  4: 'Contracted',
  5: 'Pending Termination',
  6: 'Terminated',
  7: 'Expired',
};

/**
 * Known display labels for status values (lowercase key).
 * Contract-related keys align with CONTRACT_STATUS_LABELS; also includes boat/other codes and snake_case aliases.
 */
const STATUS_LABEL_MAP: Record<string, string> = {
  draft: 'Draft',
  awaitingcustomersignature: 'Awaiting Customer Signature',
  awaitingmarinasignature: 'Awaiting Marina Signature',
  awaitinginitialpayment: 'Awaiting Initial Payment',
  contracted: 'Contracted',
  pendingtermination: 'Pending Termination',
  pending_termination: 'Pending Termination',
  terminated: 'Terminated',
  expired: 'Expired',
  none: 'None',
  pendingsignature: 'Awaiting Customer Signature',
  pending_signature: 'Awaiting Customer Signature',
  signedbycustomer: 'Awaiting Marina Signature',
  signed_by_customer: 'Awaiting Marina Signature',
  waitingfordeposit: 'Awaiting Initial Payment',
  waiting_for_deposit: 'Awaiting Initial Payment',
  waitingforfirstpayment: 'Awaiting Initial Payment',
  waiting_for_first_payment: 'Awaiting Initial Payment',
  activationinprogress: 'Activation In Progress',
  activation_in_progress: 'Activation In Progress',
  signedbyadmin: 'Signed By Admin',
  signed_by_admin: 'Signed By Admin',
  active: 'Active',
  renewed: 'Renewed',
};

/**
 * Format contract/lease/boat status for display.
 * Accepts enum ID (number) from API or legacy string codes; unknown strings fall back to title case.
 */
export function formatStatusLabel(value: string | number | null | undefined): string {
  if (value == null) return '';
  if (typeof value === 'number') return CONTRACT_STATUS_LABELS[value] ?? '';
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  const mapped = STATUS_LABEL_MAP[lower];
  if (mapped) return mapped;
  const withSpaces = trimmed.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
  return withSpaces
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format full name from parts
 */
export function formatFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  middleName?: string | null | undefined
): string {
  const parts = [firstName, middleName, lastName].filter((part) => part && part.trim());
  return parts.join(' ') || '';
}

